import React, { useState } from 'react';
import { AgentExecutionFlow, ProviderExecutionInfo, ScoringResults, ExecutionEvent } from '../types';
import { ProviderResultColumn } from './ProviderResultColumn';
import { SimplifiedSearchColumn } from './SimplifiedSearchColumn';

interface MultiProviderGridProps {
  providerExecutions: ProviderExecutionInfo[];
  executionFlows: Record<string, AgentExecutionFlow>;
  isLoading: boolean;
  onRefreshProvider?: (executionId: string, providerId: string) => void;
  scoringResults?: ScoringResults;
  isSimplifiedView?: boolean;
  executionEvents?: Record<string, ExecutionEvent[]>;
  onSubmitClarification?: (executionId: string, userResponse: string) => Promise<void>;
  sessionHistory?: Array<{ prompt: string; providerExecutions: ProviderExecutionInfo[]; executionFlowsSnapshot: Record<string, AgentExecutionFlow> }>;
  currentPrompt?: string;
}

export const MultiProviderGrid: React.FC<MultiProviderGridProps> = ({
  providerExecutions,
  executionFlows,
  isLoading,
  onRefreshProvider,
  scoringResults,
  isSimplifiedView = false,
  executionEvents = {},
  onSubmitClarification,
  sessionHistory = [],
  currentPrompt
}) => {
  const [minimizedColumns, setMinimizedColumns] = useState<Set<string>>(new Set());

  if (providerExecutions.length === 0) {
    return null;
  }

  const toggleMinimize = (executionId: string) => {
    setMinimizedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(executionId)) {
        newSet.delete(executionId);
      } else {
        newSet.add(executionId);
      }
      return newSet;
    });
  };

  // Calculate column widths
  const minimizedCount = minimizedColumns.size;
  const expandedCount = providerExecutions.length - minimizedCount;
  const minimizedWidth = 40; // pixels
  const expandedWidthPercent = expandedCount > 0 
    ? `calc((100% - ${minimizedCount * minimizedWidth}px) / ${expandedCount})`
    : '100%';

  const getColumnWidth = (executionId: string) => {
    return minimizedColumns.has(executionId) ? `${minimizedWidth}px` : expandedWidthPercent;
  };

  // Build provider list for similarity display
  const allProviders = providerExecutions.map(pe => ({
    executionId: pe.executionId,
    displayName: pe.providerDisplayName
  }));

  return (
    <div data-component="MultiProviderGrid" className="flex-1 overflow-x-auto overflow-y-hidden">
      <div className="flex h-full min-w-full">
        {providerExecutions.map((providerExec) => {
          const scoreData = scoringResults?.scoredResults.find(
            s => s.clientId === providerExec.executionId
          );
          
          // Render based on view mode
          if (isSimplifiedView) {
            return (
              <SimplifiedSearchColumn
                key={providerExec.executionId}
                providerName={providerExec.providerDisplayName}
                providerId={providerExec.providerId}
                executionFlow={executionFlows[providerExec.executionId]}
                isLoading={isLoading}
                columnWidth={getColumnWidth(providerExec.executionId)}
                isMinimized={minimizedColumns.has(providerExec.executionId)}
                onToggleMinimize={() => toggleMinimize(providerExec.executionId)}
                onRefresh={onRefreshProvider ? () => onRefreshProvider(providerExec.executionId, providerExec.providerId) : undefined}
                executionEvents={executionEvents[providerExec.executionId] || []}
                onSubmitClarification={onSubmitClarification}
                previousRounds={sessionHistory.map(round => {
                  const matchingExec = round.providerExecutions.find(pe => pe.providerDisplayName === providerExec.providerDisplayName);
                  return { prompt: round.prompt, executionFlow: matchingExec ? round.executionFlowsSnapshot[matchingExec.executionId] : undefined };
                })}
                currentPrompt={currentPrompt}
              />
            );
          }
          
          return (
            <ProviderResultColumn
              key={providerExec.executionId}
              providerName={providerExec.providerDisplayName}
              providerId={providerExec.providerId}
              executionFlow={executionFlows[providerExec.executionId]}
              isLoading={isLoading}
              columnWidth={getColumnWidth(providerExec.executionId)}
              isMinimized={minimizedColumns.has(providerExec.executionId)}
              onToggleMinimize={() => toggleMinimize(providerExec.executionId)}
              onRefresh={onRefreshProvider ? () => onRefreshProvider(providerExec.executionId, providerExec.providerId) : undefined}
              scoreData={scoreData}
              similarities={scoringResults?.similarities}
              allProviders={allProviders}
              executionEvents={executionEvents[providerExec.executionId] || []}
              onSubmitClarification={onSubmitClarification}
              previousRounds={sessionHistory.map(round => {
                const matchingExec = round.providerExecutions.find(pe => pe.providerDisplayName === providerExec.providerDisplayName);
                return { prompt: round.prompt, executionFlow: matchingExec ? round.executionFlowsSnapshot[matchingExec.executionId] : undefined };
              })}
              currentPrompt={currentPrompt}
            />
          );
        })}
      </div>
    </div>
  );
};
